import { Injectable, signal } from '@angular/core';
import { AppearanceApi, type Appearance } from './appearance.api';

const DEFAULT_PRIMARY = '#000000';
const DEFAULT_FAVICON = '/favicon.png';
const COLOR_MODE_KEY = 'ob.color-mode';

export type ColorMode = 'light' | 'dark';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  readonly appearance = signal<Appearance | null>(null);
  readonly logoUrl = signal<string | null>(null);
  readonly colorMode = signal<ColorMode>('light');

  constructor(private readonly api: AppearanceApi) {
    const mode = this.readMode();
    this.colorMode.set(mode);
    this.syncDom(mode);
  }

  async load(): Promise<void> {
    try {
      const response = await this.api.get();
      this.apply(response.data);
    } catch {
      this.applyDefaults();
    }
  }

  apply(data: Appearance): void {
    this.appearance.set(data);
    this.logoUrl.set(data.logo_url);
    this.setColors(data.primary_color, data.secondary_color);
    this.setFavicon(data.favicon_url);
  }

  clear(): void {
    this.appearance.set(null);
    this.logoUrl.set(null);
    this.applyDefaults();
  }

  toggleColorMode(): void {
    const next: ColorMode = this.colorMode() === 'dark' ? 'light' : 'dark';
    this.colorMode.set(next);
    localStorage.setItem(COLOR_MODE_KEY, next);
    this.syncDom(next);
    const data = this.appearance();
    if (data) {
      this.setColors(data.primary_color, data.secondary_color);
    } else {
      this.applyDefaults();
    }
  }

  private applyDefaults(): void {
    this.setColors(DEFAULT_PRIMARY, '#FFFFFF');
    this.setFavicon(null);
  }

  private setColors(primary: string, secondary: string): void {
    const root = document.documentElement.style;
    root.setProperty('--gold', primary);
    root.setProperty('--gold-deep', darken(primary, 0.14));
    root.setProperty('--gold-soft', hexToRgba(primary, this.colorMode() === 'dark' ? 0.18 : 0.1));
    root.setProperty('--gold-ring', hexToRgba(primary, 0.16));
    if (this.colorMode() === 'light') {
      root.setProperty('--paper-2', secondary);
    } else {
      root.removeProperty('--paper-2');
    }
  }

  private setFavicon(url: string | null): void {
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      return;
    }
    if (url) {
      link.removeAttribute('type');
      link.href = url;
      return;
    }
    link.type = 'image/png';
    link.href = DEFAULT_FAVICON;
  }

  private readMode(): ColorMode {
    try {
      return localStorage.getItem(COLOR_MODE_KEY) === 'dark' ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  }

  private syncDom(mode: ColorMode): void {
    const root = document.documentElement;
    if (mode === 'dark') {
      root.setAttribute('data-theme', 'dark');
    } else {
      root.removeAttribute('data-theme');
    }
    root.style.colorScheme = mode;
  }
}

function darken(hex: string, amount: number): string {
  const value = hex.replace('#', '');
  const num = Number.parseInt(value, 16);
  const r = Math.max(0, Math.round(((num >> 16) & 255) * (1 - amount)));
  const g = Math.max(0, Math.round(((num >> 8) & 255) * (1 - amount)));
  const b = Math.max(0, Math.round((num & 255) * (1 - amount)));
  return `#${[r, g, b].map((part) => part.toString(16).padStart(2, '0')).join('')}`.toUpperCase();
}

function hexToRgba(hex: string, alpha: number): string {
  const value = hex.replace('#', '');
  const num = Number.parseInt(value, 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}
